import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import MTable from "components/Shared/MTable.tsx";
import { TextField, Button } from "react-md";
import { cloneDeep } from "lodash";
import { getAppraisalFormData, setAppraisalFormData } from "requests/homeowner/caseforms";
import responseHandler from "responseHandler";
import { connect } from "react-redux";
import { MSpinner } from "components/Shared";

const ReoRepair = props => {
  const [repairDataState, setRepairDataState] = useState({});
  const [savingData, setSavingData] = useState(false);
  const [gettingData, setGettingData] = useState(false);
  const mounted = useRef(true);

  const exportResult = useRef(null);
  // useEffect here which will give us repairData
  const getRepairData = () => {
    setGettingData(true);
    getAppraisalFormData(props.controlNumber, (response) => {
      exportResult.current = cloneDeep(response);
      setRepairDataState(response.repeats.find(r => r.id === "repairsUpdates"));
      setGettingData(false);
    }, mounted);
  };
  useEffect(() => {
    getRepairData();
    props.openPhotoTool();
  }, []);

  const handleOnChange = (row, val) => {
    let repairDataNextState = cloneDeep(repairDataState);
    repairDataNextState.groups[row.groupIndex].controls.find(c => c.id === "FN-468").value = val.toString();
    setRepairDataState(repairDataNextState);
  };
  const columnDefinitions = [
    {
      name: "Repair",
      // eslint-disable-next-line react/display-name
      dataSource: (row) => row.photoTags ? <a tabIndex="0" style={{ cursor: "pointer" }} onClick={() => { props.openPhotoTool(row.photoTags); }}>{row.label}</a> : row.label,
      addDepthWhitespace: true,
      className: "revision-source",
    },
    {
      name: "Cost ($)",
      // eslint-disable-next-line react/display-name
      dataSource: (row) => <TextField onChange={val => handleOnChange(row, val)} value={row.estimatedCost} disabled={row.groupIndex == null} />,
      width: "200px"
    }
  ];

  /* data passed to MTable should have rows in this form
  [{
    key: "" // FN-123 
    label: ""
    estimatedCost: 
    depth: 
    depthKey: 1, 2, 3...
    groupIndex: // GET POST API will be in the form of groups: [{ controls: [], ... }]. This is the index of the group. Used to update state,
    photoTags: array of strings to pass it to the phototool to filter the photos by. Will be used when a label is clicked
  },...]
  */

  const createMTableDataRows = repairData => {
    let data = [];
    let depthKey = 0;
    let repairDataWithIndex = repairData.groups.map((r, i) => ({ ...r, index: i }));
    let interiorRepairsDataRows = [];
    let exteriorRepairsDataRows = [];
    repairDataWithIndex.forEach(r => {
      // if there is a repair description. If empty there is no repair, its just an empty control
      if (r.controls.find(c => c.id === "FN-467")?.value) {
        if (r.controls.find(c => c.id === "FN-464")?.value || ["Heating", "Plumbing", "Electrical", "Other Mechanical"].includes(r.controls.find(c => c.id === "FN-466")?.value)) {
          interiorRepairsDataRows.push(r);
        } else {
          exteriorRepairsDataRows.push(r);
        }
      }
    });

    const concatStr = (str1, str2) => {
      if (str1 && str2)
        return str1 + " " + str2;
      else if (str1) return str1;
      else if (str2) return str2;
      else return "";
    };
    const roomTypeFilter = (data, roomType) => data.filter(d => concatStr(d.controls.find(c => c.id === "FN-465").value, d.controls.find(c => c.id === "FN-464").value) === roomType);
    const repairCategoryFilter = (data, repairCategory) => data.filter(d => d.controls.find(c => c.id === "FN-466").value === repairCategory);
    const sumEstimatedCost = (data) => data.reduce((a, c) => a + +c.controls.find(co => co.id === "FN-468").value, 0);
    const addGroupedRows = (repairDataRows) => {
      let roomTypeGroups = new Set();
      repairDataRows.forEach(d => roomTypeGroups.add(concatStr(d.controls.find(c => c.id === "FN-465").value, d.controls.find(c => c.id === "FN-464").value)));

      roomTypeGroups.forEach(roomType => {
        let roomTypeDataRows = roomTypeFilter(repairDataRows, roomType);
        data.push({
          label: roomType, estimatedCost: sumEstimatedCost(roomTypeDataRows), depth: 2, depthKey: ++depthKey, groupIndex: null,
          photoTags: [roomTypeDataRows[0].controls.find(c => c.id === "FN-465").value, roomTypeDataRows[0].controls.find(c => c.id === "FN-464").value]
        });

        let repairCategoryGroups = new Set();
        roomTypeDataRows.forEach(d => repairCategoryGroups.add(d.controls.find(c => c.id === "FN-466").value));

        repairCategoryGroups.forEach(repairCategory => {
          let repairCategoryDataRows = repairCategoryFilter(roomTypeDataRows, repairCategory);
          data.push({ label: repairCategory, estimatedCost: sumEstimatedCost(repairCategoryDataRows), depth: 3, depthKey: ++depthKey, groupIndex: null, photoTags: [repairCategory] });

          repairCategoryDataRows.forEach(r => {
            data.push({
              label: r.controls.find(c => c.id === "FN-467").value,
              estimatedCost: r.controls.find(c => c.id === "FN-468").value, depth: 4, depthKey: ++depthKey, groupIndex: r.index, photoTags: [r.controls.find(c => c.id === "FN-467").value]
            });
          });
        });
      });
    };

    if (interiorRepairsDataRows.length) {
      data.push({ label: "Interior Repairs", estimatedCost: sumEstimatedCost(interiorRepairsDataRows), depth: 1, depthKey: ++depthKey, groupIndex: null });

      addGroupedRows(interiorRepairsDataRows);
    }

    if (exteriorRepairsDataRows.length) {
      data.push({ label: "Exterior Repairs", estimatedCost: sumEstimatedCost(exteriorRepairsDataRows), depth: 1, depthKey: ++depthKey, groupIndex: null });

      addGroupedRows(exteriorRepairsDataRows);
    }

    return data;
  };


  const data = repairDataState?.groups ? createMTableDataRows(repairDataState) : [];

  const handleSave = () => {
    exportResult.current.repeats.find(r => r.id === "repairsUpdates").groups = repairDataState.groups;
    setSavingData(true);
    setAppraisalFormData(props.controlNumber, exportResult.current, () => {
      setSavingData(false);
      props.makeToast("Save Successful", "success");
      getRepairData();
    }, mounted);
  };
  return gettingData ? <MSpinner /> : (
    <div className="case-data-history reo-repair">
      <Button primary raised onClick={handleSave} disabled={!exportResult.current || savingData || !data.length} style={{ marginBottom: 10 }}>Save</Button>
      <MTable columnDefinitions={columnDefinitions} data={data} keyProperty="depthKey" depthProperty="depth" emptyTableText="No repairs present." />
    </div>
  );
};

ReoRepair.propTypes = {
  controlNumber: PropTypes.number,
  makeToast: PropTypes.func.isRequired,
  openPhotoTool: PropTypes.func.isRequired,
};
const mapDispatchToProps = (dispatch) => ({
  makeToast(text, type) {
    responseHandler.customToast(text, type, dispatch);
  },
});
export default connect(null, mapDispatchToProps)(ReoRepair);